// Extracted types from schema.ts to avoid dependency on generated GraphQL types
import { type ThemedAsset } from '../themed-image/ThemedImage';

import type { Money } from '@betterment/core';

export const LegalAccountTaxationType = {
  Holding_529: 'HOLDING_529',
  Hsa: 'HSA',
  Roth_401K: 'ROTH_401K',
  RothIra: 'ROTH_IRA',
  SepIra: 'SEP_IRA',
  Taxable: 'TAXABLE',
  Traditional_401K: 'TRADITIONAL_401K',
  TraditionalIra: 'TRADITIONAL_IRA',
} as const;

export type LegalAccountTaxationType = (typeof LegalAccountTaxationType)[keyof typeof LegalAccountTaxationType];

export function is401kTaxationType(taxationType: LegalAccountTaxationType | undefined): boolean {
  return (
    taxationType === LegalAccountTaxationType.Traditional_401K || taxationType === LegalAccountTaxationType.Roth_401K
  );
}

export type SecurityIdentifier = {
  id: string;
  symbol: string;
};

type NonFund = SecurityIdentifier & {
  name?: string;
  logo?: ThemedAsset | null;
  expenseRatio?: never;
  url?: never;
};

type Fund = SecurityIdentifier & {
  name?: string;
  url?: string;
  expenseRatio?: number;
  logo?: never;
};

export type FinancialSecurity = NonFund | Fund;

export type SecurityPosition = {
  id: string;
  financialSecurity: FinancialSecurity;
  amount: Money;
  shares: number;
  currentWeight: number;
  securityPositionsBySubAccount: Array<{
    amount: Money;
    shares: number;
    subAccount: {
      id: string;
      name: string;
      legalAccount?: {
        taxationType: LegalAccountTaxationType;
      };
    };
  }>;
};

export type SecurityGroupPosition = {
  id: string;
  amount: Money; // Money type from schema
  currentWeight: number; // Percent type from schema
  averageExpenseRatioPercent: number;
  drift: number;
  fundFees: Money;
  isRogueSecurity?: boolean;
  securityGroup: {
    sellOnlySubstituteSecurities?: Array<SecurityIdentifier>;
    name: string;
    description?: string | null;
    targetWeight: number;
  };
  securityPositions: Array<SecurityPosition>;
};

export type CashBalance = {
  availableBalance: Money;
  totalBalance: Money;
  anyPending: boolean;
};

type PositionReturn = {
  amount: Money;
  percent: number;
};

export type DirectSecurityPosition = {
  amount: Money;
  hasPendingOrders: boolean;
  shares: number;
  financialSecurity: FinancialSecurity;
  totalReturn?: PositionReturn | null;
  todaysReturn?: PositionReturn | null;
};

export type DirectTradingAccount = {
  id: string;
  securityPositions: Array<DirectSecurityPosition>;
};

export function groupBuyableAndSellOnlyPositions(
  sellOnlySubstituteSecurities: Array<SecurityIdentifier>,
  positions: Array<SecurityPosition>
): {
  buyablePositions: Array<SecurityPosition>;
  sellOnlySubstitutePositions: Array<SecurityPosition>;
} {
  const sellOnlyIds = new Set(sellOnlySubstituteSecurities.map(s => s.id));

  return {
    sellOnlySubstitutePositions: positions.filter(position => sellOnlyIds.has(position.financialSecurity.id)),
    buyablePositions: positions.filter(position => !sellOnlyIds.has(position.financialSecurity.id)),
  };
}
